import { useCallback, useEffect, useState } from 'react'
import { RefreshCw, ShieldCheck, Laptop, Boxes, KeyRound, Power, ExternalLink } from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Drawer, ToastProvider, useToast, apiGet, apiMutate, usePermissions } from './primitives.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { CatalogEditor } from './CatalogEditor.js'

/**
 * /settings/services — administration du hub (LOT 4).
 *
 * Les vraies données de main-courante et parc, JAMAIS le credential
 * (l'API ne le renvoie pas, même à un Super Admin). « Rafraîchir
 * maintenant » et « Tester la connexion » passent par le worker — le
 * navigateur ne parle jamais aux services.
 */

interface Svc {
  key: string; name: string; category: string
  enabled: boolean; dashboard_enabled: boolean; public_base_url: string
  refresh_interval_s: number; timeout_ms: number; stale_after_s: number | null
  sso_status: string
  service_status: string | null; service_version: string | null
  last_success_at: string | null; last_attempt_at: string | null
  seconds_since_success: number | null; stale: boolean
  last_error: string | null
  credential_configured: boolean; credential_rotated_at: string | null
  permissions_discovered: number
}
interface SvcPerm { key: string; label: string; description: string | null; category: string | null }

function age(s: number | null): string {
  if (s === null) return '—'
  if (s < 60) return `il y a ${s} s`
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`
  return `il y a ${Math.round(s / 3600)} h`
}

function ServicesInner() {
  const meta = useShellMeta()
  const toast = useToast()
  const { permissions, authenticated } = usePermissions()
  const canRefresh = authenticated && permissions.includes('service.refresh')
  const canWrite = authenticated && permissions.includes('service.write')

  const [services, setServices] = useState<Svc[]>([])
  const [permsDrawer, setPermsDrawer] = useState<{ svc: Svc; perms: SvcPerm[] } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<{ title: string; body: string; action: () => Promise<void> } | null>(null)

  const charger = useCallback(() => {
    apiGet<{ services: Svc[] }>('/api/v1/admin/services').then((j) => setServices(j.services)).catch(() => undefined)
  }, [])
  useEffect(() => {
    charger()
    const t = setInterval(charger, 30_000)
    return () => clearInterval(t)
  }, [charger])

  const rafraichir = async (svc: Svc) => {
    setBusy(svc.key)
    const r = await apiMutate<{ ok: boolean; outcome: string; duration_ms: number | null; error_class: string | null; error?: string; message?: string }>(
      'POST', `/api/v1/admin/services/${svc.key}/refresh`)
    setBusy(null)
    if (r.status === 200 && r.data.ok) {
      toast('ok', `${svc.name} : mis à jour (${Math.round((r.data.duration_ms ?? 0) / 1000)} s)`)
    } else if (r.status === 200) {
      toast('error', `${svc.name} : échec de connexion (${r.data.error_class ?? r.data.outcome}). Les dernières données fiables restent affichées.`)
    } else {
      toast('error', r.data.message ?? 'Le rafraîchissement n’a pas pu être demandé.')
    }
    charger()
  }

  const voirPermissions = (svc: Svc) => {
    apiGet<{ permissions: SvcPerm[] }>(`/api/v1/admin/services/${svc.key}/permissions`)
      .then((j) => setPermsDrawer({ svc, perms: j.permissions }))
      .catch(() => undefined)
  }

  const basculer = (svc: Svc) => setConfirm({
    title: svc.enabled ? `Désactiver ${svc.name} ?` : `Réactiver ${svc.name} ?`,
    body: svc.enabled
      ? 'Le worker cessera toute collecte et la carte disparaîtra du tableau de bord. Aucune donnée n’est supprimée — cette action est réversible.'
      : 'Le worker reprendra les collectes au prochain cycle et la carte réapparaîtra sur le tableau de bord.',
    action: async () => {
      const r = await apiMutate<{ error?: string; message?: string }>('POST', `/api/v1/admin/services/${svc.key}/${svc.enabled ? 'disable' : 'enable'}`)
      if (r.status === 200) { toast('ok', svc.enabled ? 'Service désactivé' : 'Service réactivé'); charger() }
      else if (r.data.error === 'STEPUP_REQUIRED') toast('error', 'Opération sensible : ré-authentification forte récente requise. Rien n’a changé.')
      else toast('error', r.data.message ?? 'La modification n’a pas été enregistrée.')
    },
  })

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger">
        <h1 className="lc-title">Services</h1>
        <p className="lc-subtitle">
          Les applications spécialistes reliées à Gestion par le contrat Service API v1.
        </p>

        <div className="ad-svc-grid">
          {services.map((s) => {
            const Icone = s.key === 'main-courante' ? ShieldCheck : s.key === 'parc' ? Laptop : Boxes
            const tint = !s.enabled ? '' : s.service_status === 'OK' && !s.stale ? 'lc-card--emerald' : 'lc-card--amber'
            return (
              <article key={s.key} className={`lc-card gd-card ${tint} ${!s.enabled ? 'gd-card--muted' : ''}`}>
                <span className="lc-card__edge" aria-hidden="true" />
                <div className="gd-card__top">
                  <span className={`lc-card__icon ${!s.enabled ? 'gd-icon--muted' : ''}`} aria-hidden="true"><Icone /></span>
                  {!s.enabled
                    ? <span className="lc-pill lc-pill--idle"><Power size={13} />Désactivé</span>
                    : s.stale
                      ? <span className="lc-pill lc-pill--warning">Données périmées</span>
                      : s.service_status === 'OK'
                        ? <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />Opérationnel</span>
                        : <span className="lc-pill lc-pill--warning">{s.service_status ?? 'Aucune donnée'}</span>}
                </div>
                <div className="gd-card__family">{s.category === 'SECURITY' ? 'Sécurité' : s.category === 'PARC' ? 'Parc' : s.category}</div>
                <div className="lc-card__value--word">{s.name}</div>

                <div className="ad-svc-stats">
                  <span className="ad-svc-stat">Dernier refresh <strong>{age(s.seconds_since_success)}</strong></span>
                  <span className="ad-svc-stat">Cadence <strong>{s.refresh_interval_s} s</strong></span>
                  <span className="ad-svc-stat">Périmé après <strong>{s.stale_after_s ?? '—'} s</strong></span>
                  <span className="ad-svc-stat">Version <strong>{s.service_version ?? '—'}</strong></span>
                  <span className="ad-svc-stat">SSO <strong>{s.sso_status === 'PREPARED' ? 'préparé · désactivé' : s.sso_status.toLowerCase()}</strong></span>
                  <span className="ad-svc-stat">Credential <strong>{s.credential_configured ? 'configuré' : 'absent'}</strong></span>
                  {s.credential_rotated_at ? (
                    <span className="ad-svc-stat">Dernière rotation <strong>{new Date(s.credential_rotated_at).toLocaleDateString('fr-FR')}</strong></span>
                  ) : null}
                  {s.last_error ? <span className="ad-svc-stat" style={{ color: 'var(--amber)' }}>Dernière erreur <strong>{s.last_error}</strong></span> : null}
                </div>

                <span className="lc-card__spacer" />
                <div className="ad-actions">
                  {canRefresh && s.enabled ? (
                    <button className="lc-btn" disabled={busy === s.key} onClick={() => rafraichir(s)}>
                      <RefreshCw size={15} className={busy === s.key ? 'lc-spin-icon' : ''} />
                      {busy === s.key ? 'Rafraîchissement…' : 'Rafraîchir maintenant'}
                    </button>
                  ) : null}
                  {canRefresh && s.enabled ? (
                    <button className="lc-btn lc-btn--ghost" disabled={busy === s.key} onClick={() => rafraichir(s)}
                      title="Vérifie la chaîne complète : réseau, jeton, contrat">
                      Tester la connexion
                    </button>
                  ) : null}
                  <button className="lc-btn lc-btn--ghost" onClick={() => voirPermissions(s)}>
                    <KeyRound size={15} /> {s.permissions_discovered} permissions
                  </button>
                  {canWrite ? (
                    <button className="lc-btn lc-btn--ghost" onClick={() => basculer(s)}>
                      <Power size={15} /> {s.enabled ? 'Désactiver' : 'Réactiver'}
                    </button>
                  ) : null}
                  <a className="lc-btn lc-btn--ghost" href={s.public_base_url} target="_blank" rel="noopener noreferrer">
                    Ouvrir <ExternalLink size={14} />
                  </a>
                </div>
              </article>
            )
          })}
        </div>

        <div className="adm-note" style={{ maxWidth: 720 }}>
          Le credential d’un service n’est jamais affiché — pas même ici. Son
          remplacement (rotation) suit la procédure du runbook
          <span className="ad-mono"> managed-service-troubleshooting.md</span> ;
          Gestion ne modifie jamais le jeton côté application cible.
        </div>

        <Drawer
          open={permsDrawer !== null}
          title={permsDrawer ? `Permissions · ${permsDrawer.svc.name}` : ''}
          onClose={() => setPermsDrawer(null)}
        >
          {permsDrawer ? (
            <>
              <div className="adm-note">
                <strong>Catalogue descriptif.</strong> Ces droits sont déclarés et
                appliqués par {permsDrawer.svc.name} — l’administration centralisée
                depuis Gestion n’est pas encore activée.
              </div>
              {[...new Set(permsDrawer.perms.map((p) => p.category ?? 'Autres'))].map((cat) => (
                <div key={cat} className="ad-perm-group">
                  <div className="ad-perm-group__title">{cat}</div>
                  {permsDrawer.perms.filter((p) => (p.category ?? 'Autres') === cat).map((p) => (
                    <div key={p.key} className="ad-perm">
                      <span className="ad-perm__text">
                        <div className="ad-perm__label">{p.label}</div>
                        {p.description ? <div className="ad-perm__desc">{p.description}</div> : null}
                        <div className="ad-perm__key">{p.key}</div>
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </>
          ) : null}
        </Drawer>

        <CatalogEditor />

        <ConfirmDialog
          open={confirm !== null}
          danger
          title={confirm?.title ?? ''}
          confirmLabel="Confirmer"
          onConfirm={() => { const c = confirm; setConfirm(null); void c?.action() }}
          onClose={() => setConfirm(null)}
        >
          {confirm?.body}
        </ConfirmDialog>
      </main>
    </Shell>
  )
}

export function ServicesPage() {
  return <ToastProvider><ServicesInner /></ToastProvider>
}
